import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (password !== confirm) { toast.error("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <Link to="/login" className="flex items-center mb-10"><Logo className="text-2xl" showDot /></Link>
        {!token ? (
          <div className="text-center" data-testid="reset-invalid">
            <h1 className="font-serif text-3xl mb-2">Link not valid</h1>
            <p className="text-muted-foreground text-sm mb-8">This reset link is missing or malformed. Please request a new one.</p>
            <Link to="/forgot-password" className="text-primary text-sm" data-testid="reset-request-new">Request a new link</Link>
          </div>
        ) : done ? (
          <div className="text-center" data-testid="reset-done">
            <CheckCircle2 className="w-12 h-12 text-teal-500 mx-auto mb-5" />
            <h1 className="font-serif text-3xl mb-2">Password updated</h1>
            <p className="text-muted-foreground text-sm mb-8">You can now log in with your new password.</p>
            <Button className="rounded-full w-full" onClick={() => navigate("/login")} data-testid="reset-go-login">Log in</Button>
          </div>
        ) : (
          <>
            <h1 className="font-serif text-4xl mb-2">Choose a new password</h1>
            <p className="text-muted-foreground mb-8 text-sm">Make it something you'll remember.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="pw">New password</Label>
                <Input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="reset-password-input" className="mt-1.5 rounded-full" placeholder="••••••••" />
              </div>
              <div>
                <Label htmlFor="pw2">Confirm password</Label>
                <Input id="pw2" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm-input" className="mt-1.5 rounded-full" placeholder="••••••••" />
              </div>
              <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="reset-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
              </Button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
